import L from 'leaflet'
import type { FondDeCarte, MagasinFonds } from './fondsDeCarte'

/**
 * Contrôle « Fond de carte » : un bouton discret en haut à droite de chaque
 * carte, qui déplie la liste des fonds (le fond actif est coché), un bouton
 * « Retirer » devant chaque fond ajouté, et « Ajouter un fond… » qui ouvre un
 * petit formulaire (nom, adresse des tuiles, attribution, zoom max facultatif).
 *
 * Construit en DOM natif et `textContent` : aucun texte venu de l'utilisateur
 * (nom d'un fond ajouté, message d'erreur) n'est jamais interprété comme du
 * HTML. Le style est dans `fondDeCarte.css` (classes `fond-carte`).
 *
 * Copie IDENTIQUE dans les trois consoles ; la logique (validation, mémoire)
 * est dans `fondsDeCarte.ts`, déjà testée.
 */

const NS = 'fond-carte'
let sequence = 0

const ICONE_COUCHES =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
  '<path d="M12 3 3 8l9 5 9-5-9-5Z"/><path d="m3 12.5 9 5 9-5"/><path d="m3 17 9 5 9-5"/></svg>'
const ICONE_RETRAIT =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" ' +
  'stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>'

function el<K extends keyof HTMLElementTagNameMap>(tag: K, classe?: string, texte?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (classe) e.className = classe
  if (texte !== undefined) e.textContent = texte
  return e
}

function bouton(classe: string, texte: string): HTMLButtonElement {
  const b = el('button', classe, texte)
  b.type = 'button'
  return b
}

export function creerControleFond(magasin: MagasinFonds): L.Control {
  const controle = new L.Control({ position: 'topright' })
  let nettoyer: (() => void) | null = null

  controle.onAdd = (carte: L.Map): HTMLElement => {
    const id = ++sequence
    const racine = el('div', `leaflet-control ${NS}`)
    // Ni clic, ni molette, ni clavier ne doivent « traverser » vers la carte (déplacer, zoomer, poser une épingle).
    L.DomEvent.disableClickPropagation(racine)
    L.DomEvent.disableScrollPropagation(racine)
    L.DomEvent.on(racine, 'keydown keyup keypress', L.DomEvent.stopPropagation)

    const idPanneau = `${NS}-panneau-${id}`
    const declencheur = bouton(`${NS}__bouton`, '')
    declencheur.setAttribute('aria-expanded', 'false')
    declencheur.setAttribute('aria-controls', idPanneau)
    declencheur.innerHTML = `${ICONE_COUCHES}<span class="${NS}__libelle">Fond de carte</span>`

    const panneau = el('div', `${NS}__panneau`)
    panneau.id = idPanneau
    panneau.hidden = true
    panneau.setAttribute('role', 'group')
    panneau.setAttribute('aria-label', 'Fond de carte')

    const liste = el('div', `${NS}__liste`)
    liste.setAttribute('role', 'radiogroup')
    liste.setAttribute('aria-label', 'Choisir le fond de carte')

    const note = el('p', `${NS}__note`, 'Ce navigateur ne mémorise pas les réglages : le choix ne sera pas conservé.')
    note.hidden = true

    const basculeAjout = bouton(`${NS}__ajouter`, 'Ajouter un fond…')
    basculeAjout.setAttribute('aria-expanded', 'false')

    /* ── Formulaire d'ajout ── */
    const formulaire = el('form', `${NS}__form`)
    formulaire.hidden = true
    formulaire.noValidate = true
    const champs: Record<'nom' | 'url' | 'attribution' | 'zoomMax', HTMLInputElement> = {
      nom: el('input'),
      url: el('input'),
      attribution: el('input'),
      zoomMax: el('input'),
    }
    const ligneChamp = (cle: keyof typeof champs, libelle: string, aide?: string) => {
      const idChamp = `${NS}-${id}-${cle}`
      const champ = champs[cle]
      champ.id = idChamp
      champ.type = 'text'
      champ.autocomplete = 'off'
      champ.setAttribute('autocapitalize', 'none')
      champ.spellcheck = false
      const label = el('label', `${NS}__champ`)
      label.htmlFor = idChamp
      label.append(el('span', `${NS}__etiquette`, libelle), champ)
      if (aide) {
        const idAide = `${idChamp}-aide`
        const p = el('small', `${NS}__aide`, aide)
        p.id = idAide
        champ.setAttribute('aria-describedby', idAide)
        label.append(p)
      }
      return label
    }
    champs.nom.maxLength = 40
    champs.url.inputMode = 'url'
    champs.url.placeholder = 'https://serveur/{z}/{x}/{y}.png'
    champs.attribution.maxLength = 200
    champs.attribution.placeholder = '© OpenStreetMap contributors'
    champs.zoomMax.inputMode = 'numeric'
    champs.zoomMax.maxLength = 2
    champs.zoomMax.placeholder = '19'

    const erreur = el('p', `${NS}__erreur`)
    erreur.setAttribute('role', 'alert')
    erreur.hidden = true
    const valider = el('button', `${NS}__valider`, 'Ajouter')
    valider.type = 'submit'
    const annuler = bouton(`${NS}__annuler`, 'Annuler')
    const actions = el('div', `${NS}__actions`)
    actions.append(valider, annuler)

    formulaire.append(
      el('p', `${NS}__titre`, 'Ajouter un fond'),
      erreur,
      ligneChamp('nom', 'Nom'),
      ligneChamp('url', 'Adresse des tuiles', 'https obligatoire, avec {z}, {x} et {y}.'),
      ligneChamp('attribution', 'Attribution', 'Source de la carte, en texte simple (obligatoire).'),
      ligneChamp('zoomMax', 'Zoom max (facultatif)'),
      el('small', `${NS}__aide`, 'Ce fond reste sur cet appareil et ce navigateur.'),
      actions,
    )

    panneau.append(liste, note, basculeAjout, formulaire)
    racine.append(declencheur, panneau)

    /* ── Liste des fonds ── */
    const nomRadios = `${NS}-${id}`
    let signature = ''
    const dessinerLigne = (f: FondDeCarte): HTMLElement => {
      const ligne = el('div', `${NS}__ligne`)
      const label = el('label', `${NS}__choix`)
      const radio = el('input')
      radio.type = 'radio'
      radio.name = nomRadios
      radio.value = f.id
      radio.checked = f.id === magasin.actif().id
      radio.addEventListener('change', () => {
        if (radio.checked) magasin.choisir(f.id)
      })
      label.append(radio, el('span', `${NS}__nom`, f.nom))
      ligne.append(label)
      if (f.perso) {
        const retrait = bouton(`${NS}__retirer`, '')
        retrait.innerHTML = ICONE_RETRAIT
        retrait.setAttribute('aria-label', `Retirer le fond ${f.nom}`)
        retrait.title = 'Retirer ce fond'
        retrait.addEventListener('click', (e) => {
          // Ce bouton disparaît avec sa ligne : sans cela Leaflet, qui remonte la
          // hiérarchie de l'élément pour savoir si le clic vient d'un contrôle,
          // le prendrait pour un clic sur la carte (et refermerait le panneau).
          e.stopPropagation()
          magasin.retirer(f.id)
          basculeAjout.focus()
        })
        ligne.append(retrait)
      }
      return ligne
    }
    const synchroniser = () => {
      const fonds = magasin.fonds()
      const actif = magasin.actif().id
      const nouvelle = fonds.map((f) => f.id).join('|')
      if (nouvelle === signature) {
        // Même liste : on ne touche qu'à la coche (le focus clavier reste en place).
        liste.querySelectorAll<HTMLInputElement>('input[type=radio]').forEach((r) => {
          r.checked = r.value === actif
        })
      } else {
        const focusDansListe = liste.contains(document.activeElement)
        const valeurFocus = document.activeElement instanceof HTMLInputElement ? document.activeElement.value : null
        liste.replaceChildren(...fonds.map(dessinerLigne))
        signature = nouvelle
        if (focusDansListe && valeurFocus) liste.querySelector<HTMLInputElement>(`input[value="${CSS.escape(valeurFocus)}"]`)?.focus()
      }
      note.hidden = magasin.stockageDisponible() || !formulaire.hidden
    }

    /* ── Ouverture / fermeture ── */
    const ajusterHauteur = () => {
      const taille = carte.getSize()
      // Une carte de 220 px de haut ne doit pas être débordée par le panneau : il défile.
      panneau.style.maxHeight = `${Math.max(140, taille.y - 64)}px`
      // Et sur une carte étroite (téléphone), le panneau ne doit pas couvrir les boutons de zoom, à gauche.
      panneau.style.width = `${Math.min(268, Math.max(200, taille.x - 64))}px`
    }
    const fermer = () => {
      if (panneau.hidden) return
      panneau.hidden = true
      declencheur.setAttribute('aria-expanded', 'false')
    }
    const ouvrir = () => {
      synchroniser()
      ajusterHauteur()
      panneau.hidden = false
      declencheur.setAttribute('aria-expanded', 'true')
    }
    declencheur.addEventListener('click', () => (panneau.hidden ? ouvrir() : fermer()))
    racine.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || panneau.hidden) return
      fermer()
      declencheur.focus()
    })

    const afficherFormulaire = (visible: boolean) => {
      // Le formulaire REMPLACE la liste : sur une carte basse, les deux ne tiendraient pas ensemble.
      formulaire.hidden = !visible
      liste.hidden = visible
      basculeAjout.hidden = visible
      note.hidden = visible || magasin.stockageDisponible()
      basculeAjout.setAttribute('aria-expanded', String(visible))
      if (visible) champs.nom.focus()
      else basculeAjout.focus()
    }
    const effacerErreur = () => {
      erreur.hidden = true
      erreur.textContent = ''
      Object.values(champs).forEach((c) => c.removeAttribute('aria-invalid'))
    }
    basculeAjout.addEventListener('click', () => afficherFormulaire(true))
    annuler.addEventListener('click', () => {
      formulaire.reset()
      effacerErreur()
      afficherFormulaire(false)
    })
    Object.values(champs).forEach((c) => c.addEventListener('input', effacerErreur))
    formulaire.addEventListener('submit', (e) => {
      e.preventDefault()
      const r = magasin.ajouter({
        nom: champs.nom.value,
        url: champs.url.value,
        attribution: champs.attribution.value,
        zoomMax: champs.zoomMax.value,
      })
      if (!r.ok) {
        erreur.textContent = r.erreur
        erreur.hidden = false
        // Le champ en cause : l'utilisateur n'a pas à le chercher.
        const fautif = champs[r.champ ?? 'url']
        fautif.setAttribute('aria-invalid', 'true')
        // Le message est en haut du formulaire : on le garde à l'écran même sur une carte basse.
        fautif.focus({ preventScroll: true })
        formulaire.scrollTop = 0
        return
      }
      formulaire.reset()
      effacerErreur()
      afficherFormulaire(false)
    })

    const desabonner = magasin.abonner(synchroniser)
    carte.on('click', fermer)
    carte.on('resize', ajusterHauteur)
    synchroniser()

    nettoyer = () => {
      desabonner()
      carte.off('click', fermer)
      carte.off('resize', ajusterHauteur)
    }
    // `map.remove()` ne rappelle pas `onRemove` des contrôles : sans cela, l'abonnement fuirait à chaque écran quitté.
    carte.once('unload', () => {
      nettoyer?.()
      nettoyer = null
    })
    return racine
  }

  controle.onRemove = () => {
    nettoyer?.()
    nettoyer = null
  }
  return controle
}
